import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Flex, Text, Table, Tbody, Tr, Td, Box, Spacer, Button } from '@chakra-ui/react';
import Locale from '../../utils/translations';
import useTranslationsSummary from '../hooks/useTranslationsSummary';
import TranslationSummaryModel from '../../models/translations/translationsSummary';
import { LikeButton, DislikeButton } from '../icons/icons';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './css/DatePicker.css';

export const getDateDaysBack = (daysBack: number) => {
	const date = new Date();
	date.setDate(date.getDate() - daysBack);
	return date;
};

const LanguageDetectionSummaryListItem = ({
	translationSummary,
	from,
	to,
}: {
	translationSummary: TranslationSummaryModel;
	from?: Date | null;
	to?: Date | null;
}) => {
	const navigate = useNavigate();

	const fmt = (val: number) => (isNaN(val) ? '-' : val.toFixed(2));

	return (
		<Tr bg="white" borderRadius="4px" fontWeight="600" fontSize="14px">
			<Td
				border="1px solid #F5F1F1"
				borderRight="none"
				p="8px 16px"
				borderStartRadius="4px"
			/>

			<Td borderTop="1px solid #F5F1F1" display="grid" gridTemplateColumns="auto 1fr">
				<Text variant="beTextDescription" color="#1462F0">
					Recognized language:
				</Text>
				<Text variant="beTextDescription" color="#1462F0" ml={'20px'}>
					{translationSummary.lang}
					<Button
						size="xs"
						float="right"
						onClick={() =>
							navigate(
								`/language-detection?${new URLSearchParams({
									lang: translationSummary.lang,
									from: from?.toISOString() || '',
									to: to?.toISOString() || '',
								}).toString()}`,
							)
						}
					>
						show details
					</Button>
				</Text>
				<Text variant="beTextDescription" color="#1462F0">
					Confidence:
				</Text>
				<Text variant="beTextDescription">
					{translationSummary.count > 1 && (
						<Text as="span" ml="20px">
							min: {fmt(translationSummary.minConfidence)}
						</Text>
					)}
					<Text as="span" ml="20px">
						avg: {fmt(translationSummary.sumConfidence / translationSummary.count)}
					</Text>
					{translationSummary.count > 1 && (
						<Text as="span" ml="20px">
							max: {fmt(translationSummary.maxConfidence)}
						</Text>
					)}
					<Text as="span" ml="20px">
						count: {translationSummary.count}
					</Text>
				</Text>
				<Text variant="beTextDescription" color="#1462F0">
					Answers:
				</Text>
				<Text variant="beTextDescription">
					{translationSummary.answers.map((item: any, index: number) => {
						let answer = item.answer;
						let color = 'black';
						if (!answer) {
							answer = (
								<Text as="span" color="gray">
									no answer
								</Text>
							);
							color = 'gray';
						} else if (answer == 'yes') {
							answer = (
								<Text as="span" color="green">
									<LikeButton fill="green" /> yes
								</Text>
							);
							color = 'green';
						} else if (answer == 'no') {
							answer = (
								<Text as="span" color="red">
									<DislikeButton fill="red" /> no
								</Text>
							);
							color = 'red';
						}
						return (
							<Box key={index} color={color}>
								{translationSummary.count > 1 && (
									<Text as="span" ml="20px">
										min: {fmt(item.minConfidence)}
									</Text>
								)}
								<Text as="span" ml="20px">
									avg: {fmt(item.sumConfidence / item.count)}
								</Text>
								{translationSummary.count > 1 && (
									<Text as="span" ml="20px">
										max: {fmt(item.maxConfidence)}
									</Text>
								)}
								<Text as="span" ml="20px">
									count: {item.count}
								</Text>
								<Text as="span" ml="20px">
									{answer} {item.count}
								</Text>
							</Box>
						);
					})}
				</Text>
			</Td>
		</Tr>
	);
};
const LanguageDetectionSummary: React.FC = () => {
	const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
	const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([
		getDateDaysBack(10),
		new Date(),
	]);
	const [localStartDate, localEndDate] = dateRange;
	const [filterDateRange, setFilterDateRange] = React.useState<[Date | null, Date | null]>();

	const {
		translationsSummary,
		translationsSummaryLoading,
		// moveNext,
		// movePrev,
		// offset,
		// pageSize,
		//mutateTranslationsSummary,
	} = useTranslationsSummary(
		'language_detection',
		filterDateRange?.[0],
		filterDateRange?.[1]
			? moment(filterDateRange[1]).add(1, 'day').toDate()
			: filterDateRange?.[1],
	);

	useEffect(() => {
		setFilterDateRange(dateRange);
	}, []);

	const handleDateChange = (dates: [Date | null, Date | null]) => {
		const [start, end] = dates;
		setDateRange([start, end]);
	};

	if (translationsSummaryLoading) {
		return <div>loading ...</div>;
	}

	return (
		<Box display={'flex'} flexWrap={'wrap'} justifyContent={'center'}>
			<Flex alignItems="center" justifyContent="space-between" w={'60%'}>
				<Text fontSize="18px" fontWeight="700">
					{Locale.get('Language detection summary')}
				</Text>
				<Spacer />
				<DatePicker
					onChange={handleDateChange}
					onClickOutside={() => setIsCalendarOpen(false)}
					onInputClick={() => setIsCalendarOpen(true)}
					startDate={localStartDate}
					endDate={localEndDate}
					selectsRange={true}
					maxDate={new Date()}
					isClearable={false}
					className="date-picker-input-field"
					placeholderText="Click to select a date range"
					open={isCalendarOpen}
					disabledKeyboardNavigation={true}
				>
					<Button
						display={'flex'}
						margin={'auto'}
						size="sm"
						borderWidth="1px"
						borderRadius="sm"
						borderColor="transparent"
						bgColor={'#1E599E'}
						color={'white'}
						onClick={() => {
							setIsCalendarOpen(false);
							setFilterDateRange(dateRange);
						}}
					>
						Apply
					</Button>
				</DatePicker>
			</Flex>
			<Table
				mt="23px"
				variant="unstyled"
				style={{ borderCollapse: 'separate', borderSpacing: '0 0.5em' }}
				w={'60%'}
			>
				<Tbody>
					{translationsSummary?.map(
						(translationSummary: TranslationSummaryModel, index: number) => (
							<LanguageDetectionSummaryListItem
								translationSummary={translationSummary}
								key={index}
								from={filterDateRange?.[0]}
								to={filterDateRange?.[1]}
							/>
						),
					)}
				</Tbody>
			</Table>
		</Box>
	);
};

export default LanguageDetectionSummary;
